import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { storeStockStyle } from './store-stock.style.js'
import { STORE_LOCATOR_PROXY_PATH, STORE_LOCATOR_STORE, STORES_PATH, StoreLocatorStore } from 'components/discovery/store-locator/store-locator-store.js'
import { STORE_MAP_CONFIGURATION_STORE, StoreMapConfigurationStore } from 'components/discovery/store-locator/components/store-map/store-map-config-store.js'
import 'components/discovery/store-locator/components/store-search-field/store-search-field.js'
import 'components/discovery/pdp/store-stock/components/store-stock-list/store-stock-list.js'

@customElement('csc-store-stock')
export class CSCStoreStock extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, storeStockStyle]

  @inject(STORE_LOCATOR_STORE)
  private storeLocatorStore!: StoreLocatorStore

  @inject(STORE_MAP_CONFIGURATION_STORE)
  private storeMapConfigurationStore!: StoreMapConfigurationStore

  @property({ type: Number, attribute: 'product-id' })
  productId!: number

  @property({ type: String, attribute: 'country-code' })
  countryCode = ''

  @property({ type: String, attribute: 'maps-key' })
  mapsKey = ''

  @state()
  private onlyInStock = false

  override connectedCallback() {
    super.connectedCallback()
    this.initializeStoreLocatorStore()
  }

  private initializeStoreLocatorStore = async () => {
    const storesEndpoint = (await createUrl(STORES_PATH)).href
    this.storeLocatorStore.setStoresPath(storesEndpoint)

    this.storeLocatorStore.setProxy((await createUrl(STORE_LOCATOR_PROXY_PATH)).href)
    if (this.countryCode) this.storeLocatorStore.setCountry(this.countryCode)
    if (this.mapsKey) this.storeMapConfigurationStore.setMapsApiKey(this.mapsKey)

    this.storeLocatorStore.getStores(this.storeLocatorStore.mapCoordinates())
  }

  private handleToggleChange = (event: Event): void => {
    const toggle = event.target as HTMLInputElement
    this.onlyInStock = toggle?.checked ?? this.onlyInStock
  }

  private renderFilterToggle = () => html`
    <cse-group class="filter-row" alignment="center" column-gap="03">
      <cse-text as="span" variant="body">
        ${t('only_in_stock_label', { ns: 'csc-store-stock', default: 'Only show stores with stock' })}
      </cse-text>
      <cse-toggle
        ?checked="${this.onlyInStock}"
        @change="${this.handleToggleChange}"
        aria-label="${t('only_in_stock_label', { ns: 'csc-store-stock', default: 'Only show stores with stock' })}"
      ></cse-toggle>
    </cse-group>
  `

  override render() {
    return html`
      <csc-store-search-field></csc-store-search-field>
      ${this.renderFilterToggle()}
      <csc-store-stock-list
        product-id="${this.productId}"
        ?only-in-stock="${this.onlyInStock}"
      ></csc-store-stock-list>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-store-stock': CSCStoreStock
  }
}
